/** DriverStats — stats overview tab for driver race history (backed by GetDriverStats RPC) */
import { useState, useEffect, useCallback } from 'react';
import { rpc, RpcError } from '@/api/client';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { DriverView } from './DriverView';
import type { GetDriverStatsResponse, DriverStats as DriverStatsType } from '@/types/telemetry';

interface DriverStatsProps {
  driverId: string;
}

export function DriverStats({ driverId }: DriverStatsProps) {
  const { t } = useI18n();
  const [data, setData] = useState<GetDriverStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<GetDriverStatsResponse>('GetDriverStats', { driver_id: driverId });
      setData(res);
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested driver could not be found.' : (e.message || t('races.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <DriverView driverId={driverId} activeTab="stats">
      {loading && <LoadingSpinner />}
      {error && <ErrorState message={error} isNotFound={notFound} />}
      {data && data.stats && <StatsContent stats={data.stats} />}
    </DriverView>
  );
}

function StatsContent({ stats }: { stats: DriverStatsType }) {
  const { t } = useI18n();

  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm border-collapse">
        <tbody className="divide-y divide-border-subtle">
          {/* Race counts */}
          <StatRow label={t('races.raceStarts')} value={stats.starts} />
          <StatRow label={t('races.statWins')} value={stats.wins}>
            {stats.wins > 0 && (
              <span className="text-text-muted text-xs ml-1.5">
                ({stats.winRate.toFixed(1)}%)
              </span>
            )}
          </StatRow>
          <StatRow label={t('races.podiums')} value={stats.podiums}>
            {stats.podiums > 0 && (
              <span className="text-text-muted text-xs ml-1.5">
                ({stats.podiumRate.toFixed(1)}%)
                <span className="ml-2">
                  {stats.p1 > 0 && <span className="text-yellow ml-1">🥇{stats.p1}</span>}
                  {stats.p2 > 0 && <span className="text-text-secondary ml-1">🥈{stats.p2}</span>}
                  {stats.p3 > 0 && <span className="text-text-muted ml-1">🥉{stats.p3}</span>}
                </span>
              </span>
            )}
          </StatRow>
          <StatRow label={t('races.qualiPoles')} value={stats.qualiPoles} />
          <StatRow label={t('races.dnfDsq')} value={stats.dnfDsq} />

          {/* Position stats */}
          <StatRow
            label={t('races.statAvgFinish')}
            value={stats.avgFinishPosition > 0 ? `P${stats.avgFinishPosition.toFixed(1)}` : '—'}
          />
          <StatRow
            label={t('races.statTotalLaps')}
            value={stats.totalLapsCompleted}
          />

          {/* Breadth */}
          <StatRow label={t('races.statSeasons')} value={stats.seasons} />
          <StatRow label={t('races.statTracks')} value={stats.tracks} />
          <StatRow
            label={t('races.statCategories')}
            value={stats.categories && stats.categories.length > 0 ? stats.categories.join(', ') : '—'}
            mono={false}
          />
        </tbody>
      </table>
    </div>
  );
}

function StatRow({ label, value, valueClass, mono = true, children }: {
  label: string;
  value: string | number;
  valueClass?: string;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <tr className="hover:bg-bg-hover transition-colors">
      <td className="px-4 py-2.5 text-text-muted whitespace-nowrap">{label}</td>
      <td className={`px-4 py-2.5 text-right font-medium ${mono ? 'mono' : ''} ${valueClass || ''}`}>
        {value}
        {children}
      </td>
    </tr>
  );
}
